import { valibotResolver } from "@hookform/resolvers/valibot";
import { useForm } from "react-hook-form";

import useAllLabelsMutations from "features/labels/datahooks/useAllLabelsMutations";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import { DialogTitle } from "common/components/ui/Dialog";
import { DrawerTitle } from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import { getSchemaMaxLength, typedObjectKeys } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { LABEL_COLORS } from "../../constants";
import { LabelSchema, type LabelSchemaType } from "../../schemas";
import LabelColorPicker from "./LabelColorPicker";

import type { Label, LabelType } from "../../types";

interface LabelFormProps {
  label?: Label;
  onDelete?: () => void;
  onBack: () => void;
  type: LabelType;
}

export default function LabelForm({
  label = null,
  onDelete = undefined,
  onBack,
  type,
}: LabelFormProps) {
  const isDesktop = useTwBreakpoint("lg");

  const { createLabel, isCreatingLabel, updateLabel, isUpdatingLabel } =
    useAllLabelsMutations(type);

  const isLoading = isCreatingLabel || isUpdatingLabel;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<LabelSchemaType>({
    defaultValues: {
      name: label ? label.name : "",
      color: label ? label.color : typedObjectKeys(LABEL_COLORS)[0],
    },
    resolver: valibotResolver(LabelSchema),
  });

  function onSave(newLabel: LabelSchemaType) {
    if (label) {
      updateLabel({ label: newLabel, labelId: label.id }).then(() => onBack());
    } else {
      createLabel(newLabel).then(() => onBack());
    }
  }

  const Title = isDesktop ? DialogTitle : DrawerTitle;

  const selectedColor = watch("color");
  const updatedName = watch("name");

  return (
    <>
      <Title className="mb-4">{label ? "Edit" : "Create new"} label</Title>
      <FormField
        size="lg"
        label="Label name"
        htmlFor="label-name"
        error={errors.name?.message}
      >
        <Input
          id="label-name"
          placeholder="Label name"
          autoFocus
          maxLength={getSchemaMaxLength(LabelSchema.entries.name)}
          variant="lg"
          data-cy="label-name-input"
          {...register("name")}
        />
      </FormField>

      <LabelColorPicker
        selectedColor={selectedColor}
        setSelectedColor={(color) => setValue("color", color)}
      />

      <div className="mt-8 flex gap-x-2.5 justify-between">
        {label ? (
          <RenderIf condition={!isDesktop}>
            <Button variant="outline-danger" onClick={onDelete}>
              Delete
            </Button>
          </RenderIf>
        ) : (
          <Button variant="outline-black" onClick={onBack} disabled={isLoading}>
            Discard
          </Button>
        )}

        <Button
          onClick={handleSubmit(onSave)}
          disabled={
            label && label.name === updatedName && label.color === selectedColor
          }
          isLoading={isLoading}
          data-cy="submit-label-button"
        >
          {label ? "Save" : "Create"}
        </Button>
      </div>
    </>
  );
}
